import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Hosts from '../../../core/functions/hosts'

type CeletusSellerOnboardingCardProps = {
  /**
   * Exibe o componente (use isso para condicionar ao "primeiro produto" do usuário)
   */
  open?: boolean

  /**
   * Nome do usuário (opcional, só para personalizar o texto)
   */
  userName?: string

  /**
   * Contatos (placeholders fáceis de trocar depois)
   */
  supportEmail?: string
  supportPhoneDisplay?: string // ex.: "(11) 99999-9999"
  supportWhatsAppUrl?: string // ex.: "https://wa.me/5511999999999"

  /**
   * Ação ao clicar no CTA. Se não vier, o componente simula envio.
   */
  onRequestToSell?: () => Promise<void> | void
}

const REQUEST_BUTTON_TEXT =
  'Entrar em contato para iniciar a validação'.toUpperCase()

export function CeletusSellerOnboardingCard({
  open = true,
  userName,
  supportEmail = 'suporte@celetus.com.br',
  supportPhoneDisplay = '(11) 99999-9999',
  supportWhatsAppUrl = 'https://wa.me/5500000000000',
  onRequestToSell,
}: CeletusSellerOnboardingCardProps) {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const greeting = useMemo(() => {
    if (!userName) return 'Bem-vindo(a) à Celetus.'
    return `Bem-vindo(a), ${userName}.`
  }, [userName])

  const handleRequest = async () => {
    setSuccess(false)
    setLoading(true)

    try {
      if (onRequestToSell) {
        await onRequestToSell()
      } else {
        // Simulação (trocar por chamada real depois)
        await new Promise((r) => setTimeout(r, 900))
      }
      setSuccess(true)
    } finally {
      setLoading(false)
    }
  }

  const supportWhatsAppUrlWithText = useMemo(() => {
    const text = encodeURIComponent(
      `Olá, gostaria de iniciar o processo para vender na Celetus.`
    )
    return `${supportWhatsAppUrl}?text=${text}`
  }, [supportWhatsAppUrl])

  return (
    <Collapse in={open} unmountOnExit>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2.25,
            bgcolor: 'background.default',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'action.hover',
              }}
            >
              <LockOutlinedIcon fontSize="small" />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Segurança para novos vendedores
                </Typography>
                <Chip
                  size="small"
                  icon={<VerifiedUserOutlinedIcon />}
                  label="Verificação inicial"
                  variant="outlined"
                />
              </Stack>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.3 }}
              >
                {greeting} Para proteger compradores e vendedores, o primeiro
                produto passa por um fluxo de validação antes de ficar
                disponível para venda.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <CardContent sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            {/* Alerts */}
            {success ? (
              <Alert severity="success" icon={<VerifiedUserOutlinedIcon />}>
                Solicitação enviada com sucesso. Nossa equipe vai analisar seus
                dados e orientar os próximos passos.
              </Alert>
            ) : (
              <Alert severity="info" icon={<InfoOutlinedIcon />}>
                Ao clicar em <strong>“{REQUEST_BUTTON_TEXT}”</strong>, você
                inicia o processo de habilitação de vendedor. Em seguida, vamos
                solicitar algumas informações e documentos para validação.
              </Alert>
            )}

            {/* Conteúdo principal */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Stack spacing={1.25}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Como funciona
                  </Typography>

                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <ChecklistOutlinedIcon
                        fontSize="small"
                        sx={{ mt: 0.3 }}
                      />
                      <Typography variant="body2">
                        Você confirma que deseja vender na Celetus e envia as
                        informações básicas do seu negócio.
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <ChecklistOutlinedIcon
                        fontSize="small"
                        sx={{ mt: 0.3 }}
                      />
                      <Typography variant="body2">
                        Nossa equipe valida o cadastro (antifraude +
                        conformidade) para reduzir riscos e proteger a
                        plataforma.
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <ChecklistOutlinedIcon
                        fontSize="small"
                        sx={{ mt: 0.3 }}
                      />
                      <Typography variant="body2">
                        Após aprovado, você conclui o cadastro do produto e
                        ativa as vendas com checkout e rastreamento.
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <ChecklistOutlinedIcon
                        fontSize="small"
                        sx={{ mt: 0.3 }}
                      />
                      <Typography variant="body2">
                        Em caso de pendência, você recebe instruções do que
                        ajustar para seguir com a liberação.
                      </Typography>
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 0.5 }} />

                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    O que você precisa ter em mãos
                  </Typography>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <StorefrontOutlinedIcon
                        fontSize="small"
                        sx={{ mt: 0.3 }}
                      />
                      <Typography variant="body2">
                        Dados do responsável e do negócio (nome, CPF/CNPJ,
                        endereço e contato).
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <StorefrontOutlinedIcon
                        fontSize="small"
                        sx={{ mt: 0.3 }}
                      />
                      <Typography variant="body2">
                        Informações do produto: descrição clara, política de
                        entrega/acesso, e suporte ao cliente.
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <StorefrontOutlinedIcon
                        fontSize="small"
                        sx={{ mt: 0.3 }}
                      />
                      <Typography variant="body2">
                        Materiais de venda (opcional, mas recomendado): página
                        de vendas, criativos, prova social.
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} md={5}>
                <Stack spacing={1.5} sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      Precisa de ajuda?
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Fale com a equipe. Se preferir, envie prints do que está
                      tentando cadastrar.
                    </Typography>

                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <SupportAgentOutlinedIcon fontSize="small" />
                        <Typography variant="body2">
                          E-mail:{' '}
                          <Link
                            href={`mailto:${supportEmail}`}
                            underline="hover"
                          >
                            {supportEmail}
                          </Link>
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <SupportAgentOutlinedIcon fontSize="small" />
                        <Typography variant="body2">
                          Telefone:{' '}
                          <Link
                            href={`tel:${supportPhoneDisplay.replace(/\D/g, '')}`}
                            underline="hover"
                          >
                            {supportPhoneDisplay}
                          </Link>
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <SupportAgentOutlinedIcon fontSize="small" />
                        <Typography variant="body2">
                          WhatsApp:{' '}
                          <Link
                            href={supportWhatsAppUrlWithText}
                            target="_blank"
                            rel="noreferrer"
                            underline="hover"
                          >
                            Abrir conversa
                          </Link>
                        </Typography>
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="caption" color="text.secondary">
                      Observação: a Celetus pode solicitar validações adicionais
                      conforme o tipo de produto e o perfil de risco. Isso faz
                      parte do nosso processo de segurança e prevenção a
                      fraudes.
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={() =>
                      Hosts.openNewBlank(supportWhatsAppUrlWithText)
                    }
                    disabled={loading}
                    sx={{
                      py: 1.4,
                      borderRadius: 3,
                      fontWeight: 900,
                      letterSpacing: 0.2,
                      textTransform: 'uppercase',
                    }}
                  >
                    {REQUEST_BUTTON_TEXT}
                  </Button>

                  {/*<Button
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={handleRequest}
                    disabled={loading}
                    sx={{
                      py: 1.4,
                      borderRadius: 3,
                      fontWeight: 900,
                      letterSpacing: 0.2,
                      textTransform: 'uppercase',
                    }}
                  >
                    {loading
                      ? 'Enviando solicitação...'
                      : 'QUERO VENDER PARA A CELETUS'}
                  </Button>*/}

                  {/*!success && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textAlign: 'center' }}
                    >
                      Ao continuar, você confirma que as informações fornecidas
                      serão verdadeiras e que seguirá as políticas da
                      plataforma.
                    </Typography>
                  )*/}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>
    </Collapse>
  )
}
